import React, { useState, useRef, useMemo } from 'react';
import Button from '../common/Button';
import { useRequiredDocuments, useMyDocuments, useUploadDocument } from '../../hooks/useUserQueries';
import './Documents.css';

const Documents = ({ data = {}, onNext, onPrevious, onSkip, isSubmitting = false }) => {
  // Fetch required documents from backend
  // User should be logged in after email verification, so tokens should be available
  const { data: requiredDocumentsData, isLoading: isLoadingRequired, isError: isErrorRequired, error: errorRequired } = useRequiredDocuments();
  // Fetch user's uploaded documents
  const { data: myDocumentsData, isLoading: isLoadingDocuments, isError: isErrorDocuments } = useMyDocuments();
  const uploadDocumentMutation = useUploadDocument();
  
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const fileInputRefs = useRef({});

  // Create a map of uploaded documents by document_type_id
  const uploadedDocumentsMap = useMemo(() => {
    const map = {};
    if (myDocumentsData && Array.isArray(myDocumentsData)) {
      myDocumentsData.forEach(doc => {
        const docTypeId = doc.document_type;
        if (docTypeId) {
          map[docTypeId] = doc;
        }
      });
    }
    return map;
  }, [myDocumentsData]);

  const isLoading = isLoadingRequired || isLoadingDocuments;

  // Get required and optional documents from backend data
  const requiredDocs = useMemo(() => {
    if (!requiredDocumentsData || !Array.isArray(requiredDocumentsData)) return [];
    // Handle both is_required and isRequired (camelCase)
    return requiredDocumentsData.filter(doc => doc.is_required === true || doc.isRequired === true);
  }, [requiredDocumentsData]);

  const optionalDocs = useMemo(() => {
    if (!requiredDocumentsData || !Array.isArray(requiredDocumentsData)) return [];
    // Handle both is_required and isRequired (camelCase)
    return requiredDocumentsData.filter(doc => !doc.is_required && !doc.isRequired);
  }, [requiredDocumentsData]);

  const handleFileSelect = (documentTypeId, e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(documentTypeId, file);
    }
  };

  const handleFileUpload = async (documentTypeId, file) => {
    setUploadingDocId(documentTypeId);
    try {
      // Backend expects documents array with document_type_id and file
      await uploadDocumentMutation.mutateAsync({
        documents: [{
          document_type_id: documentTypeId,
          file: file,
        }],
      });
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploadingDocId(null);
      // Reset file input
      if (fileInputRefs.current[documentTypeId]) {
        fileInputRefs.current[documentTypeId].value = '';
      }
    }
  };

  const handleCardClick = (documentTypeId) => {
    const existingDoc = uploadedDocumentsMap[documentTypeId];
    if (!existingDoc && !uploadingDocId) {
      fileInputRefs.current[documentTypeId]?.click();
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Proceed to next step
    if (onNext) {
      onNext();
    }
  };

  const handleSkip = () => {
    // Skip document upload
    if (onSkip) {
      onSkip();
    }
  };

  const getCompletedRequiredCount = () => {
    return requiredDocs.filter(doc => uploadedDocumentsMap[doc.id]).length;
  };

  const getTotalRequiredCount = () => {
    return requiredDocs.length;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="documents">
        <div className="documents__header">
          <p className="documents__subtitle">Loading documents...</p>
        </div>
      </div>
    );
  }

  // Show error state if API fails
  if (isErrorRequired) {
    console.error('Error loading required documents:', errorRequired);
  }

  return (
    <div className="documents">
      <div className="documents__header">
        {getTotalRequiredCount() > 0 && (
          <div className="documents__progress">
            <div className="documents__progress-text">
              {getCompletedRequiredCount()} of {getTotalRequiredCount()} required document{getTotalRequiredCount() !== 1 ? 's' : ''} uploaded
            </div>
            <div className="documents__progress-bar">
              <div 
                className="documents__progress-fill"
                style={{ width: `${getTotalRequiredCount() > 0 ? (getCompletedRequiredCount() / getTotalRequiredCount()) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="documents__form">
        <div className="documents__content">
          {isErrorRequired ? (
            <div className="documents__section">
              <div className="documents__error-state">
                <p className="documents__error-text">
                  Unable to load documents. You can skip this step and upload documents later from your profile.
                </p>
              </div>
            </div>
          ) : requiredDocs.length > 0 && (
            <div className="documents__section">
              <h3 className="documents__section-title">Required Documents</h3>
              <p className="documents__section-description">
                These documents are mandatory to work as a dental professional in the UK
              </p>
              <div className="documents__cards-grid">
                {requiredDocs.map((document, index) => {
                  const existingDoc = uploadedDocumentsMap[document.id];
                  const isUploading = uploadingDocId === document.id;
                  return (
                    <div
                      key={document.id || index}
                      className={`document-card ${existingDoc ? 'document-card--uploaded' : ''} ${isUploading ? 'document-card--uploading' : ''}`}
                      onClick={() => handleCardClick(document.id)}
                    >
                      <input
                        ref={el => fileInputRefs.current[document.id] = el}
                        type="file"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => handleFileSelect(document.id, e)}
                        style={{ display: 'none' }}
                        disabled={isUploading || !!existingDoc}
                      />
                      
                      <div className="document-card__header">
                        <div className="document-card__title">
                          {document.name || document.document_type_name || `Document ${document.id}`}
                          <span className="document-card__required">*</span>
                        </div>
                        <div className="document-card__status">
                          {isUploading ? (
                            <div className="document-card__status-icon document-card__status-icon--uploading">⟳</div>
                          ) : existingDoc ? (
                            <div className="document-card__status-icon document-card__status-icon--uploaded">✓</div>
                          ) : (
                            <div className="document-card__status-icon document-card__status-icon--empty">+</div>
                          )}
                          <span className="document-card__status-text">
                            {isUploading ? 'Uploading...' : existingDoc ? 'Uploaded' : 'Click to upload'}
                          </span>
                        </div>
                      </div>

                      <div className="document-card__body">
                        {existingDoc ? (
                          <div className="document-card__file-info">
                            <div className="document-card__file-details">
                              <div className="document-card__file-name">{existingDoc.file_name || 'Document uploaded'}</div>
                              {existingDoc.file_size && (
                                <div className="document-card__file-size">
                                  {formatFileSize(existingDoc.file_size)}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="document-card__description">
                            {document.description || 'Upload your document'}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) }

          {optionalDocs.length > 0 && (
            <div className="documents__section">
              <h3 className="documents__section-title">Optional Documents</h3>
              <p className="documents__section-description">
                Uploading these documents will make more job opportunities available to you
              </p>
              <div className="documents__cards-grid">
                {optionalDocs.map(document => {
                  const existingDoc = uploadedDocumentsMap[document.id];
                  const isUploading = uploadingDocId === document.id;
                  return (
                    <div
                      key={document.id}
                      className={`document-card ${existingDoc ? 'document-card--uploaded' : ''} ${isUploading ? 'document-card--uploading' : ''}`}
                      onClick={() => handleCardClick(document.id)}
                    >
                      <input
                        ref={el => fileInputRefs.current[document.id] = el}
                        type="file"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => handleFileSelect(document.id, e)}
                        style={{ display: 'none' }}
                        disabled={isUploading || !!existingDoc}
                      />
                      
                      <div className="document-card__header">
                        <div className="document-card__title">
                          {document.name || document.document_type_name || `Document ${document.id}`}
                        </div>
                        <div className="document-card__status">
                          {isUploading ? (
                            <div className="document-card__status-icon document-card__status-icon--uploading">⟳</div>
                          ) : existingDoc ? (
                            <div className="document-card__status-icon document-card__status-icon--uploaded">✓</div>
                          ) : (
                            <div className="document-card__status-icon document-card__status-icon--empty">+</div>
                          )}
                          <span className="document-card__status-text">
                            {isUploading ? 'Uploading...' : existingDoc ? 'Uploaded' : 'Click to upload'}
                          </span>
                        </div>
                      </div>

                      <div className="document-card__body">
                        {existingDoc ? (
                          <div className="document-card__file-info">
                            <div className="document-card__file-details">
                              <div className="document-card__file-name">{existingDoc.file_name || 'Document uploaded'}</div>
                              {existingDoc.file_size && (
                                <div className="document-card__file-size">
                                  {formatFileSize(existingDoc.file_size)}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="document-card__description">
                            {document.description || 'Upload your document'}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {requiredDocumentsData && requiredDocs.length === 0 && optionalDocs.length === 0 && !isLoading && (
            <div className="documents__section">
              <div className="documents__empty-state">
                <p className="documents__empty-text">
                  No documents required for your role at this time. You can continue to the next step.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="documents__actions">
          {onSkip && (
            <Button 
              type="button" 
              variant="outline" 
              size="medium"
              onClick={handleSkip}
              className="documents__button"
              disabled={isSubmitting || uploadDocumentMutation.isPending}
            >
              Skip for Now
            </Button>
          )}
          <Button 
            type="submit" 
            variant="primary" 
            size="medium"
            className="documents__button"
            disabled={isSubmitting || uploadDocumentMutation.isPending}
            loading={isSubmitting || uploadDocumentMutation.isPending}
          >
            {isSubmitting || uploadDocumentMutation.isPending ? 'Processing...' : 'Continue'}
          </Button>
        </div>
        {getTotalRequiredCount() > 0 && getCompletedRequiredCount() < getTotalRequiredCount() && (
          <div className="documents__skip-notice">
            <strong>Note:</strong> You have {getTotalRequiredCount() - getCompletedRequiredCount()} required document{getTotalRequiredCount() - getCompletedRequiredCount() !== 1 ? 's' : ''} remaining. 
            You can upload these later from your profile, but they are mandatory to work as a dental professional in the UK.
          </div>
        )}
      </form>
    </div>
  );
};

export default Documents;
